import { useRef, useEffect, useState } from "react";
import { motion, useInView } from "motion/react";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

interface Message {
  sender: "client" | "ai";
  text: string;
}

interface ConversationProps {
  title: string;
  messages: Message[];
  delay: number;
  shouldAnimate: boolean;
}

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const messagesContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};

const messageVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function ConversationCard({ title, messages, delay, shouldAnimate }: ConversationProps) {
  return (
    <motion.div
      className="rounded-2xl p-6 max-w-md w-full"
      style={{
        background: "var(--surface-1)",
        border: "1px solid var(--border)",
        boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
      }}
      variants={cardVariants}
      custom={delay}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
    >
      <div
        className="flex items-center gap-3 mb-6 pb-4"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: "rgba(212, 149, 106, 0.10)",
            color: "var(--gold)",
          }}
        >
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <h3
          className="text-sm font-medium flex-1"
          style={{ fontFamily: "var(--font-body)", color: "var(--cream-dim)" }}
        >
          {title}
        </h3>
        <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
      </div>

      <motion.div
        className="space-y-3"
        variants={messagesContainerVariants}
        initial="hidden"
        animate={shouldAnimate ? "visible" : "hidden"}
      >
        {messages.map((message, index) => (
          <motion.div
            key={index}
            className={`flex ${message.sender === "client" ? "justify-end" : "justify-start"}`}
            variants={messageVariants}
          >
            <div
              className="rounded-2xl px-4 py-2.5 max-w-[82%]"
              style={
                message.sender === "client"
                  ? {
                      background: "var(--surface-3)",
                      color: "var(--foreground)",
                      borderBottomRightRadius: "4px",
                      fontFamily: "var(--font-body)",
                      fontSize: "0.8rem",
                      lineHeight: "1.5",
                    }
                  : {
                      background: "rgba(212, 149, 106, 0.14)",
                      border: "1px solid rgba(212, 149, 106, 0.22)",
                      color: "var(--cream)",
                      borderBottomLeftRadius: "4px",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.78rem",
                      lineHeight: "1.55",
                    }
              }
            >
              <p>{message.text}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}

export function ConversationExamples() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0 });
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const shouldAnimate = isInView || fallback;

  const { language } = useLanguage();
  const t = translations[language].conversations;

  const conversations = t.cards.map((card, i) => ({
    ...card,
    delay: [0, 0.2, 0.4][i],
  }));

  return (
    <section
      className="pt-14 pb-14 px-6 relative"
      style={{
        background: `
          radial-gradient(ellipse 70% 60% at 10% 50%, var(--glow-purple) 0%, transparent 65%),
          radial-gradient(ellipse 50% 40% at 90% 80%, var(--glow-gold) 0%, transparent 60%),
          var(--background)
        `,
      }}
    >
      <div ref={sectionRef} className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          {conversations.map((conv, index) => (
            <ConversationCard key={index} {...conv} shouldAnimate={shouldAnimate} />
          ))}
        </div>
      </div>
    </section>
  );
}
